/**
 * Indication Backfill · Data Ops · Jul 2026
 *
 * Populates the blank `indication` field on assets via Perplexity live web
 * search of the asset / company pipeline. Captures all diseases the asset
 * targets (comma-separated). Simple Processed / Updated / Failed progress.
 * Never overwrites an existing indication.
 *
 * Route: /data-ops/indication-backfill
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Stethoscope, PlayCircle, StopCircle, CheckCircle2, AlertCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

export default function IndicationBackfillPage() {
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const p = await axios.get(`${API}/api/data-ops/indication-backfill/preview`, { withCredentials: true });
      setPreview(p.data);
    } catch { /* no-op */ }
    try {
      const s = await axios.get(`${API}/api/data-ops/indication-backfill/status`, { withCredentials: true });
      setJob(s.data.job || null);
    } catch { /* no-op */ }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const running = job && ['queued', 'running'].includes(job.status);

  useEffect(() => {
    if (!running) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/data-ops/indication-backfill/status?job_id=${job.job_id}`, { withCredentials: true });
        if (r.data?.job) setJob(r.data.job);
      } catch { /* no-op */ }
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job?.job_id, running]); // eslint-disable-line

  const doStart = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/indication-backfill/start`, {}, { withCredentials: true });
      setJob(r.data.job);
      toast.success(`Indication backfill started · ${preview?.total || 0} assets queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start');
    } finally { setStarting(false); }
  };

  const doCancel = async () => {
    if (!job) return;
    try {
      await axios.post(`${API}/api/data-ops/indication-backfill/cancel`, { job_id: job.job_id }, { withCredentials: true });
      toast.info('Cancelled');
      refresh();
    } catch { toast.error('Cancel failed'); }
  };

  const pct = job?.total ? Math.round(((job.processed || 0) / job.total) * 100) : 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="indication-backfill-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Indication Backfill</h1>
          <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">
            Populates the blank <code className="text-[11px] bg-slate-100 rounded px-1">Indication</code> field on assets via <strong>Perplexity web search</strong> of the asset / company pipeline, press &amp; ClinicalTrials.gov. Captures <strong>all diseases</strong> the asset targets (comma-separated). Existing indications are <strong>never overwritten</strong>.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-1">
          <Stethoscope className="w-3 h-3" /> AI Data Ops
        </span>
      </div>

      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 space-y-3" data-testid="indication-backfill-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="text-[12px] text-slate-600 inline-flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-rose-600" /> Multi-indication assets get all relevant diseases, primary first.
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right mr-1">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Blank indication</div>
              <div className="text-2xl font-black tabular-nums text-slate-900" data-testid="indication-backfill-total">
                {(preview?.total ?? 0).toLocaleString()}
              </div>
            </div>
            {!running ? (
              <Button
                onClick={doStart}
                disabled={!preview?.total || starting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="indication-backfill-run"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                Run Indication Backfill
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={doCancel}
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="indication-backfill-cancel"
              >
                <StopCircle className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
          </div>
        </div>

        {job && (
          <div>
            <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1 flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5">
                {running && <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-600" />}
                {job.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                {job.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-rose-600" />}
                <span className="font-semibold">{job.status}</span>
                {job.current && running && <span className="text-slate-400">now: {job.current}</span>}
              </span>
              <span className="flex gap-2" data-testid="indication-backfill-progress">
                <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">Processed: <b className="tabular-nums">{job.processed || 0}</b> / {job.total || 0}</span>
                <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Updated: <b className="tabular-nums">{job.updated || 0}</b></span>
                <span className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-rose-700">Failed: <b className="tabular-nums">{job.failed || 0}</b></span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full transition-all ${running ? 'bg-rose-500' : job.status === 'done' ? 'bg-emerald-500' : 'bg-slate-400'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {job.status === 'done' && (
              <div className="mt-1 text-[11px] text-emerald-700">
                Done · {job.updated} of {job.total} assets updated with an indication ({job.failed} could not be resolved).
              </div>
            )}
            {job.error && <div className="mt-1 text-[11px] text-rose-700">{job.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
